'use client';

import dynamic from 'next/dynamic';
import { Suspense, useCallback, useRef, useState } from 'react';

import { ChatbotButton, type ChatbotButtonVariant } from './chatbot-button/ChatbotButton';

// ChatbotModal은 createPortal(react-dom) + useExternalStoreRuntime을 사용하므로
// SSR(legacy Pages Router, main App Router 모두) 호환을 위해 ssr:false 필수.
const ChatbotModal = dynamic(
  () => import('./chatbot-modal/ChatbotModal').then((m) => ({ default: m.ChatbotModal })),
  { ssr: false },
);

type PendingAction = { type: 'close' } | { type: 'navigate'; href: string };

export interface ExitConfirmConfig {
  title: string[];
  description: string[];
  closeText: string[];
  confirmText: string[];
  onConfirm: () => void;
  onCancel?: () => void;
}

export interface FAQChatbotProps {
  /** 라우터 의존성 주입. Next 14 Pages Router / Next 16 App Router 차이를 흡수. */
  navigate: (href: string) => void;
  /**
   * 메시지가 있는 상태에서 닫기/이동 시도 시 종료 확인 다이얼로그를 띄우도록 consumer에 위임.
   * consumer는 자체 모달 시스템(예: Redux modalActions.openConfirmModal)으로 매핑.
   */
  requestExitConfirm: (config: ExitConfirmConfig) => void;
  /** 버튼 외형 variant. 기본값은 원형 46x46. 'pill'은 "챗봇상담" 라벨의 캡슐형. */
  variant?: ChatbotButtonVariant;
}

const EXIT_CONFIRM_TEXT = {
  title: ['대화를 종료하시겠습니까?'],
  description: ['페이지를 나가면', '대화 내용이 저장되지 않습니다.'],
  closeText: ['아니오'],
  confirmText: ['확인'],
};

export function FAQChatbot({ navigate, requestExitConfirm, variant }: FAQChatbotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const pendingActionRef = useRef<PendingAction | null>(null);
  const messageCountRef = useRef(0);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleMessageCountChange = useCallback((count: number) => {
    messageCountRef.current = count;
  }, []);

  const handleConfirmExit = useCallback(() => {
    const action = pendingActionRef.current;
    if (action?.type === 'navigate') {
      navigate(action.href);
    }
    setIsOpen(false);
    pendingActionRef.current = null;
  }, [navigate]);

  const showExitConfirm = useCallback((action: PendingAction) => {
    pendingActionRef.current = action;
    requestExitConfirm({
      ...EXIT_CONFIRM_TEXT,
      onCancel: () => { pendingActionRef.current = null; },
      onConfirm: handleConfirmExit,
    });
  }, [requestExitConfirm, handleConfirmExit]);

  const handleCloseAttempt = useCallback(() => {
    if (messageCountRef.current > 0) {
      showExitConfirm({ type: 'close' });
    } else {
      setIsOpen(false);
    }
  }, [showExitConfirm]);

  const handleNavigateAttempt = useCallback((href: string) => {
    if (messageCountRef.current > 0) {
      showExitConfirm({ type: 'navigate', href });
    } else {
      setIsOpen(false);
      navigate(href);
    }
  }, [navigate, showExitConfirm]);

  return (
    <>
      <ChatbotButton ref={triggerRef} variant={variant} isOpen={isOpen} onClick={isOpen ? handleCloseAttempt : () => setIsOpen(true)} />
      {/* ChatbotModal은 ssr:false dynamic이라 client에서 suspend → 외부 Suspense까지 올라가면 형제 노드가 함께 fallback되어 hydration mismatch 발생, 여기서 좁게 격리. */}
      <Suspense fallback={null}>
        <ChatbotModal
          isOpen={isOpen}
          onClose={handleCloseAttempt}
          onNavigate={handleNavigateAttempt}
          onMessageCountChange={handleMessageCountChange}
          triggerRef={triggerRef}
        />
      </Suspense>
    </>
  );
}
